import React from "react";
import HexColorPopover from "../HexColorPopover";
import { X, SquarePlus, Check, CalendarIcon, Clock } from "lucide-react";
import { Dialog, DialogContent } from "../Dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../Select";
import { Label } from "../Label";
import { Button } from "../Button";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { format, setHours, setMinutes } from "date-fns";
import CustomDatePicker from "../CutomDatePicker";
import { cn } from "../../../utils/classMerger";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { postTime } from "../../campaign/types";
import { CardTitle } from "../Card";

// Update your customButtonStyles:
const customButtonStyles = `
.color-custom-btn {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: none;
  padding: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ff6b6b, #ffd93d, #6bcb77, #4d96ff, #9b59b6);
}

.color-custom-btn .inner {
  background: #fff;
  border-radius: 4px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
`;

interface PostGraphicsEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    editedImageText: string,
    graphics: {
      background: { type: "solid" | "gradient"; color?: string; from?: string; to?: string; direction?: string };
      textColor: string;
      fontSize: number;
      fontFamily: string;
      scheduledFor?: Date;
    }
  ) => void;
  bgColor: { type: "solid" | "gradient"; color?: string; from?: string; to?: string; direction?: string };
  textColor: string;
  fontSize: number;
  fontFamily: string;
  setBgColor: React.Dispatch<React.SetStateAction<{ type: "solid" | "gradient"; color?: string; from?: string; to?: string; direction?: string }>>;
  setTextColor: React.Dispatch<React.SetStateAction<string>>;
  setFontSize: React.Dispatch<React.SetStateAction<number>>;
  setFontFamily: React.Dispatch<React.SetStateAction<string>>;
  imageText: string;
  setImageText: React.Dispatch<React.SetStateAction<string>>;
  previewText?: string;
  // Add scheduling props
  scheduledFor?: Date;
  setScheduledFor?: React.Dispatch<React.SetStateAction<Date>>;
  // Add custom font size props
  hasCustomFontSize?: boolean;
  customFontSize?: number;
}

const PostGraphicsEditor: React.FC<PostGraphicsEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  bgColor,
  textColor,
  fontSize,
  fontFamily,
  setBgColor,
  setTextColor,
  setFontSize,
  setFontFamily,
  imageText,
  setImageText,
  previewText,
  scheduledFor,
  setScheduledFor,
  hasCustomFontSize = false,
  customFontSize,
}) => {
  // A Lead Generation post is its graphic: the image text is the only text it carries.
  const [localImageText, setLocalImageText] = React.useState<string>(imageText);

  // Local state for graphics settings — only flushed to parent on Save
  const [localBgColor, setLocalBgColor] = React.useState(bgColor);
  const [localTextColor, setLocalTextColor] = React.useState<string>(textColor);
  const [localFontSize, setLocalFontSize] = React.useState<number>(fontSize);
  const [localFontFamily, setLocalFontFamily] = React.useState<string>(fontFamily);
  const [localScheduledFor, setLocalScheduledFor] = React.useState<Date>(
    scheduledFor ? new Date(scheduledFor) : new Date()
  );

  // Derived local bg fields for solid/gradient controls
  const [bgType, setBgType] = React.useState<"solid" | "gradient">(bgColor.type);
  const [gradientFrom, setGradientFrom] = React.useState(bgColor.from || "#2563eb");
  const [gradientTo, setGradientTo] = React.useState(bgColor.to || "#ec4899");
  const [gradientDirection, setGradientDirection] = React.useState(bgColor.direction || "to right");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [googleFonts, setGoogleFonts] = React.useState<string[]>([]);
  
  // Track if dialog was just opened
  const wasOpenRef = React.useRef(false);

  // Sync prop changes to local state ONLY when dialog transitions from closed to open
  React.useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Dialog just opened — freeze all initial values
      setLocalImageText(imageText);
      setLocalBgColor(bgColor);
      setLocalTextColor(textColor);
      setLocalFontSize(fontSize);
      setLocalFontFamily(fontFamily);
      setLocalScheduledFor(scheduledFor ? new Date(scheduledFor) : new Date());
      // Sync derived gradient state
      setBgType(bgColor.type);
      setGradientFrom(bgColor.from || "#2563eb");
      setGradientTo(bgColor.to || "#ec4899");
      setGradientDirection(bgColor.direction || "to right");
      wasOpenRef.current = true;
    } else if (!isOpen && wasOpenRef.current) {
      wasOpenRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Custom font size states - initialize with custom size if provided
  const [localCustomFontSize, setLocalCustomFontSize] = React.useState<number | string>(
    hasCustomFontSize && customFontSize ? customFontSize : localFontSize || 24
  );
  const [pendingFontSize, setPendingFontSize] = React.useState<number | string | null>(null);
  const [isEditingCustomSize, setIsEditingCustomSize] = React.useState(false);
  const IMAGE_TEXT_LIMIT = 200;

  // Add scheduling state and handlers
  const isMobile = useIsMobile(900);
  const [date, setDate] = React.useState<Date>(
    scheduledFor ? new Date(scheduledFor) : new Date()
  );
  const [isCalendarOpen, setIsCalendarOpen] = React.useState<boolean>(false);

   // Load Google Fonts list
    React.useEffect(() => {
      const loadGoogleFonts = async () => {
        try {
          const apiKey = process.env.REACT_APP_GOOGLE_FONTS_API_KEY;
          const response = await fetch(
            `https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}&sort=popularity`,
          );
          const data = await response.json();
          const fontNames = data.items
  .map((font: { family: string }) => font.family)
  .filter(
    (font: string) =>
      font !== "Material Icons" &&
      font !== "Material Icons Outlined" &&
      font !== "Material Symbols Outlined" &&
      font !== "Material Symbols Rounded" && font !== "Archivo Black"
  );
          setGoogleFonts(fontNames);
        } catch (error) {
          console.error("Failed to load Google Fonts:", error);
          // Fallback to popular fonts
          setGoogleFonts([
          "Archivo",
          "Arimo",
          "Barlow",
          "Bebas Neue",
          "DM Sans",
          "Figtree",
          "Fira Sans",
          "Google Sans",
          "Heebo",
          "IBM Plex Sans",
          "Inconsolata",
          "Inter",
          "Kanit",
          "Lato",
          "Lobster Two",
          "Lora",
          "Manrope",
          "Merriweather",
          "Montserrat",
          "Mulish",
          "Noto Sans",
          "Noto Sans JP",
          "Noto Sans KR",
          "Noto Sans TC",
          "Noto Serif",
          "Nunito",
          "Nunito Sans",
          "Open Sans",
          "Oswald",
          "Outfit",
          "Playfair Display",
          "Poppins",
          "Prompt",
          "PT Sans",
          "Quicksand",
          "Raleway",
          "Roboto",
          "Roboto Condensed",
          "Roboto Mono",
          "Roboto Slab",
          "Rubik",
          "Saira",
          "Titillium Web",
          "Ubuntu",
          "Work Sans",
          ]);
        }
      };
      loadGoogleFonts();
    }, []);
    
  // Time state (24-hour format internally)
  const initialHour = scheduledFor
    ? new Date(scheduledFor).getHours()
    : 0;
  const initialMinute = scheduledFor
    ? new Date(scheduledFor).getMinutes()
    : 0;
  const [time, setTime] = React.useState<postTime>({
    hour: initialHour,
    minute: initialMinute,
  });

  // Keep localScheduledFor in sync with local date/time pickers
  React.useEffect(() => {
    const updated = setHours(setMinutes(new Date(date), time.minute), time.hour);
    setLocalScheduledFor(updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, time]);

  // Compute period from time (24-hour format)
  const period = time.hour >= 12 ? "PM" : "AM";

  // Convert 24-hour hour to 12-hour format for the UI
  const displayHour = (() => {
    const h = time.hour % 12;
    return h === 0 ? 12 : h;
  })();

  // Hour and minute options
  const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i);

  // Handler functions
  const handleHourChange = (value: string) => {
    const selectedHour12 = parseInt(value, 10);
    let newHour = selectedHour12;
    if (period === "PM" && selectedHour12 !== 12) {
      newHour = selectedHour12 + 12;
    } else if (period === "AM" && selectedHour12 === 12) {
      newHour = 0;
    }
    setTime((prev) => ({ ...prev, hour: newHour }));
  };

  const handleMinuteChange = (value: string) => {
    const newMinute = parseInt(value, 10);
    setTime((prev) => ({ ...prev, minute: newMinute }));
  };

  const handlePeriodChange = (value: string) => {
    const currentDisplayHour = time.hour % 12 === 0 ? 12 : time.hour % 12;
    const newHour =
      value === "AM"
        ? currentDisplayHour === 12
          ? 0
          : currentDisplayHour
        : currentDisplayHour === 12
        ? 12
        : currentDisplayHour + 12;
    setTime((prev) => ({ ...prev, hour: newHour }));
  };

  // Dynamically load selected Google Font whenever local font family changes
  React.useEffect(() => {
    if (localFontFamily && !document.getElementById(`font-${localFontFamily.replace(/\s/g, "-")}`)) {
      const link = document.createElement("link");
      link.id = `font-${localFontFamily.replace(/\s/g, "-")}`;
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=${localFontFamily.replace(/\s/g, "+")}:wght@400;500;600;700&display=swap`;
      document.head.appendChild(link);
    }
  }, [localFontFamily]);

  // Keep localBgColor in sync with local gradient controls
  React.useEffect(() => {
    if (bgType === "gradient") {
      setLocalBgColor({
        type: "gradient",
        from: gradientFrom,
        to: gradientTo,
        direction: gradientDirection,
      });
    } else {
      // For solid, localBgColor.color is managed directly by the color buttons
      setLocalBgColor((prev) => ({ ...prev, type: "solid" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgType, gradientFrom, gradientTo, gradientDirection]);

  const backgroundStyle =
    bgType === "solid"
      ? localBgColor.color || "#000000"
      : `linear-gradient(${gradientDirection}, ${gradientFrom}, ${gradientTo})`;

      function getMaxFontSize(text: string) {
        const len = text.length;
        if (len <= 15) return 72;
        if (len <= 40) return 56;
        if (len <= 80) return 44;
        if (len < 120) return 36;
        if (len < 160) return 28;
        if (len <= 200) return 20;
        return 16;
      }

      const imageTextString = Array.isArray(localImageText)
        ? localImageText.join(" ")
        : localImageText || "";

      const maxFontSize = getMaxFontSize(imageTextString);

      // Dynamically generate allowed font sizes for dropdown
      const defaultSizes = [16, 20, 24, 32, 36, 40, 44, 48, 56, 72].filter(
        (size) => size <= maxFontSize,
      );

      const effectiveFontSize = Math.min(
          typeof localFontSize === "number"
          ? localFontSize
          : parseInt(String(localFontSize) || "24", 10),
        maxFontSize,
      );

   React.useEffect(() => {
  const text = Array.isArray(localImageText)
    ? localImageText.join(" ")
    : localImageText || "";

  const max = getMaxFontSize(text);
  const allowedSizes = [16, 20, 24, 32, 36, 40, 44, 48, 56, 72].filter((size) => size <= max);

  // If custom size exceeds new max, clear it and fall back to a regular value
  if (
    typeof localCustomFontSize === "number" &&
    localCustomFontSize > max
  ) {
    setLocalCustomFontSize("");
    // Fallback: set local font size to the largest allowed regular value
    if (allowedSizes.length > 0) {
      setLocalFontSize(allowedSizes[allowedSizes.length - 1]);
    } else {
      setLocalFontSize(max);
    }
  } else if (typeof localFontSize === "number" && localFontSize > max) {
    // If current localFontSize is also invalid, reset it
    if (allowedSizes.length > 0) {
      setLocalFontSize(allowedSizes[allowedSizes.length - 1]);
    } else {
      setLocalFontSize(max);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [localImageText]);

  // Flush all local state to parent setters, then call onSave
  const handleSave = () => {
    setBgColor(localBgColor);
    setTextColor(localTextColor);
    setFontSize(localFontSize);
    setFontFamily(localFontFamily);
    if (setScheduledFor) {
      setScheduledFor(localScheduledFor);
    }
    setImageText(localImageText);
    // Pass all graphics values directly so the parent callback receives fresh data
    // (parent state setters above are async and may not have committed yet)
    onSave(localImageText, {
      background: localBgColor,
      textColor: localTextColor,
      fontSize: localFontSize,
      fontFamily: localFontFamily,
      scheduledFor: localScheduledFor,
    });
  };

  // Reset local state back to initial prop values and close without saving
  const handleCancel = () => {
    setLocalBgColor(bgColor);
    setLocalTextColor(textColor);
    setLocalFontSize(fontSize);
    setLocalFontFamily(fontFamily);
    const initialDate = scheduledFor ? new Date(scheduledFor) : new Date();
    setLocalScheduledFor(initialDate);
    // Reset the date/time picker UI state
    setDate(initialDate);
    setTime({ hour: initialDate.getHours(), minute: initialDate.getMinutes() });
    setLocalImageText(imageText);
    setBgType(bgColor.type);
    setGradientFrom(bgColor.from || "#2563eb");
    setGradientTo(bgColor.to || "#ec4899");
    setGradientDirection(bgColor.direction || "to right");
    onClose();
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: customButtonStyles }} />

      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={`relative max-w-4xl w-full overflow-y-auto bg-white ${isMobile ? "px-1 max-h-[85vh] my-4" : "px-6 max-h-[90vh]"}`}>
          <button
            onClick={onClose}
            className="absolute top-2 right-4 p-2  rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 hover:text-primary" />
          </button>
          <div className="border rounded-lg pt-3 py-6 shadow-sm px-2 mt-5">
            {/* Header */}
            <div className={`flex flex-col items-center justify-center pb-6 text-center ${isMobile ? "px-1" : "px-6"}`}>
              <div>
                <CardTitle className="px-6 font-semibold text-primary">
                  Edit Post Graphics
                </CardTitle>
                <p className="text-gray-500 text-md mt-3 max-w-2xl ">
                  Choose background, text colors, font family, and size to
                  design how your promotional announcements will appear o social
                  media.
                </p>
              </div>
            </div>

            <div className={`space-y-2 ${isMobile ? "px-1" : "px-6"}`}>
              {/* Preview */}
              <div
                className={`w-full mx-auto rounded-xl flex items-center justify-center text-center transition-all overflow-hidden ${isMobile ? "min-h-[160px] p-4 border-gray-300" : "min-h-[200px] p-6"}`}
                style={{
                  background: backgroundStyle,
                  color: localTextColor,
                  fontFamily: localFontFamily,
                  fontSize: `${effectiveFontSize}px`,
                  border: "3px dashed #9CA3AF",
                }}
              >
                <p className="font-semibold leading-relaxed break-words max-w-full">
                  {localImageText || "Your post image text will appear here"}
                </p>
              </div>

              {/* Post Image Text */}
              <div>
                <div className="flex justify-between items-center mt-4">
                  <Label
                    className="text-sm text-gray-500 uppercase"
                    style={{ fontWeight: "bolder", letterSpacing: "0.05em" }}
                  >
                    POST IMAGE TEXT
                  </Label>
                  <span
                    className={`text-xs ml-2 ${
                      localImageText.length > IMAGE_TEXT_LIMIT
                        ? "text-red-500"
                        : "text-gray-400"
                    }`}
                  >
                    {localImageText.length} / {IMAGE_TEXT_LIMIT}
                  </span>
                </div>
                <textarea
                  value={localImageText}
                  onChange={(e) => {
                    if (e.target.value.length <= IMAGE_TEXT_LIMIT) {
                      setLocalImageText(e.target.value);
                    }
                  }}
                  className={`w-full min-h-[70px] p-2 border rounded-lg text-sm 
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
            resize-vertical ${
              localImageText.length > IMAGE_TEXT_LIMIT
                ? "border-red-300 bg-red-50"
                : localImageText.length > IMAGE_TEXT_LIMIT - 20
                  ? "border-yellow-300 bg-yellow-50"
                  : "border-gray-200"
            }`}
                  placeholder="Enter text that will appear on the post graphic..."
                  rows={1}
                  maxLength={IMAGE_TEXT_LIMIT}
                />
                {localImageText.length > IMAGE_TEXT_LIMIT && (
                  <p className="text-red-500 text-xs mt-1">
                    Image text exceeds {IMAGE_TEXT_LIMIT} character limit
                  </p>
                )}
              </div>

              {/* Controls Grid */}
              <div
                className={`grid gap-3 -mt-2 ${isMobile ? "grid-cols-1" : "grid-cols-2"}`}
              >
                {/* Background */}
                <div>
                  <p
                    className="text-sm text-gray-500 uppercase  mb-1 uppercase"
                    style={{ fontWeight: "bolder", letterSpacing: "0.05em" }}
                  >
                    BACKGROUND
                  </p>

                  {/* Solid Colors */}
                  {bgType === "solid" && (
                    <div
                      className={
                        isMobile
                          ? `grid gap-3 mb-4 grid-cols-5`
                          : `flex gap-3 mb-4`
                      }
                    >
                      {(() => {
                        const predefinedColors = [
                          "#000000",
                          "#ffffff",
                          "#ffe11a",
                          "#45bf55",
                          "#0288d1",
                        ];
                        const isCustomColor =
                          bgType === "solid" &&
                          !predefinedColors.includes(localBgColor.color || "");

                        return (
                          <>
                            {predefinedColors.map((color) => (
                              <button
                                key={color}
                                onClick={() => {
                                  setBgType("solid");
                                  setLocalBgColor({ type: "solid", color });
                                }}
                                className={`w-10 h-10 rounded-lg border-2 transition-all ${isMobile ? "justify-self-start" : ""} ${
                                  bgType === "solid" && localBgColor.color === color
                                    ? "border-black scale-105"
                                    : "border-gray-200 hover:border-gray-400"
                                }`}
                                style={{ backgroundColor: color }}
                              />
                            ))}

                            {/* Custom Color */}
                            <div
                              className={`relative ${isMobile ? "justify-self-start" : ""}`}
                            >
                              <div
                                className={`color-custom-btn ${isCustomColor ? "scale-105" : ""}`}
                              >
                                <div className="inner">
                                  <SquarePlus size={16} color="#5a6a7e" />
                                </div>
                              </div>
                              <HexColorPopover
                                color={localBgColor.color || "#000000"}
                                onChange={(color) => {
                                  setBgType("solid");
                                  setLocalBgColor({
                                    type: "solid",
                                    color,
                                  });
                                }}
                              >
                                <button
                                  type="button"
                                  aria-label="Custom background colour"
                                  className="absolute inset-0 cursor-pointer rounded-md opacity-0"
                                />
                              </HexColorPopover>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* Gradient Controls */}
                  {bgType === "gradient" && (
                    <div className="flex items-end gap-4 flex-wrap mb-4">
                      {/* From Color */}
                      <div className="flex flex-col">
                        <div className="relative w-10 h-10">
                          <div
                            className="w-10 h-10 flex items-center justify-center border-2 border-black rounded-md transition-all cursor-pointer scale-105"
                            style={{ backgroundColor: gradientFrom }}
                          >
                            <SquarePlus size={16} className="text-white" />
                          </div>
                          <HexColorPopover
                            color={gradientFrom}
                            onChange={setGradientFrom}
                          >
                            <button
                              type="button"
                              aria-label="Gradient start colour"
                              className="absolute inset-0 cursor-pointer rounded-md opacity-0"
                            />
                          </HexColorPopover>
                        </div>
                      </div>

                      {/* To Color */}
                      <div className="flex flex-col">
                        <div className="relative w-10 h-10">
                          <div
                            className="w-10 h-10 flex items-center justify-center border-2 border-black rounded-md transition-all cursor-pointer scale-105"
                            style={{ backgroundColor: gradientTo }}
                          >
                            <SquarePlus size={16} className="text-white" />
                          </div>
                          <HexColorPopover
                            color={gradientTo}
                            onChange={setGradientTo}
                          >
                            <button
                              type="button"
                              aria-label="Gradient end colour"
                              className="absolute inset-0 cursor-pointer rounded-md opacity-0"
                            />
                          </HexColorPopover>
                        </div>
                      </div>

                      {/* Direction */}
                      <div className={`flex flex-col ${isMobile ? "" : "ml-auto"} min-w-[160px]`}>
                        <Select
                          value={gradientDirection}
                          onValueChange={(value: string) =>
                            setGradientDirection(value)
                          }
                        >
                          <SelectTrigger className="border rounded-lg px-3 py-2 text-md">
                            <SelectValue placeholder="Select direction" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="to right">
                              Left → Right
                            </SelectItem>
                            <SelectItem value="to left">
                              Right → Left
                            </SelectItem>
                            <SelectItem value="to bottom">
                              Top → Bottom
                            </SelectItem>
                            <SelectItem value="to top">Bottom → Top</SelectItem>
                            <SelectItem value="to bottom right">
                              Diagonal ↘
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Toggle Buttons */}
                  <div className="flex bg-white border border-gray rounded-lg w-fit">
                    <button
                      onClick={() => {
                        setBgType("solid");
                        setLocalBgColor({
                          type: "solid",
                          color:
                            localBgColor.type === "solid"
                              ? localBgColor.color
                              : "#000000",
                        });
                      }}
                      className={`${isMobile ? "w-24" : "w-32"} px-4 py-1.5 rounded-md text-md transition-all font-semibold ${
                        bgType === "solid"
                          ? "bg-primary text-white shadow-sm"
                          : "text-[#4b5563] hover:text-gray-800"
                      }`}
                    >
                      Solid
                    </button>
                    <button
                      onClick={() => {
                        setBgType("gradient");
                        setLocalBgColor({
                          type: "gradient",
                          from: gradientFrom,
                          to: gradientTo,
                          direction: gradientDirection,
                        });
                      }}
                      className={`${isMobile ? "w-24" : "w-32"} px-4 py-1.5 rounded-md text-md transition-all font-semibold ${
                        bgType === "gradient"
                          ? "bg-primary text-white shadow-sm"
                          : "text-[#4b5563] hover:text-gray-800"
                      }`}
                    >
                      Gradient
                    </button>
                  </div>
                </div>

                {/* Text Color */}
                <div>
                  <p
                    className="text-sm mb-1 text-left text-gray-500"
                    style={{ fontWeight: "bolder", letterSpacing: "0.05em" }}
                  >
                    TEXT COLOR
                  </p>
                  <div
                    className={
                      isMobile ? `grid gap-3 grid-cols-5` : `flex gap-3`
                    }
                  >
                    {(() => {
                      const predefinedTextColors = [
                        "#000000",
                        "#ffffff",
                        "#ffe11a",
                        "#45bf55",
                        "#0288d1",
                      ];
                      const isCustomTextColor =
                        !predefinedTextColors.includes(localTextColor);

                      return (
                        <>
                          {predefinedTextColors.map((color) => (
                            <button
                              key={color}
                              onClick={() => setLocalTextColor(color)}
                              className={`w-10 h-10 rounded-lg border-2 transition-all ${isMobile ? "justify-self-start" : ""} ${
                                localTextColor === color
                                  ? "border-black scale-105"
                                  : "border-gray-200 hover:border-gray-400"
                              }`}
                              style={{ backgroundColor: color }}
                            />
                          ))}

                          {/* Custom Text Color */}
                          <div
                            className={`relative ${isMobile ? "justify-self-start" : ""}`}
                          >
                            <div
                              className={`color-custom-btn ${isCustomTextColor ? "scale-105" : ""}`}
                            >
                              <div className="inner">
                                <SquarePlus size={16} color="#5a6a7e" />
                              </div>
                            </div>
                            <HexColorPopover
                              color={localTextColor}
                              onChange={setLocalTextColor}
                            >
                              <button
                                type="button"
                                aria-label="Custom text colour"
                                className="absolute inset-0 cursor-pointer rounded-md opacity-0"
                              />
                            </HexColorPopover>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Font Family */}
                <div>
                  <p
                    className="text-sm text-gray-500"
                    style={{ fontWeight: "bolder", letterSpacing: "0.05em" }}
                  >
                    FONT FAMILY
                  </p>

                  <div className="mt-1">
                    <Select
                      value={localFontFamily}
                      onValueChange={(value: string) => setLocalFontFamily(value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        {[...googleFonts]
                          .slice(0, 50)
                          .sort((a, b) => a.localeCompare(b))
                          .map((font) => (
                            <SelectItem key={font} value={font}>
                              <span style={{ fontFamily: font }}>{font}</span>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Font Size */}
                <div>
                  <Label
                    className="text-sm text-gray-500"
                    style={{ fontWeight: "bolder", letterSpacing: "0.05em" }}
                  >
                    FONT SIZE
                  </Label>

                  <div className="space-y-2 w-full ">
                    {/* Show dropdown when not editing custom size */}
                    {!isEditingCustomSize && (
                      <Select
                        value={localFontSize.toString()}
                        onValueChange={(value: string) => {
                          if (value === "custom") {
                            setIsEditingCustomSize(true);
                            setPendingFontSize(
                              typeof localCustomFontSize === "number"
                                ? localCustomFontSize
                                : localFontSize || 24,
                            );
                          } else {
                            const newSize = parseInt(value);
                            setIsEditingCustomSize(false);
                            setLocalFontSize(newSize);
                          }
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select font size" />
                        </SelectTrigger>
                        <SelectContent>
                          {(() => {
                            // Check if the current localFontSize is not in the default list
                            const hasCustomSize =
                              typeof localFontSize === "number" &&
                              !defaultSizes.includes(localFontSize);
                            return (
                              <>
                                {/* Show custom size if current localFontSize is not in default list */}
                                {hasCustomSize && (
                                  <SelectItem
                                    key={`custom-${localFontSize}`}
                                    value={localFontSize.toString()}
                                  >
                                    {localFontSize}px (custom)
                                  </SelectItem>
                                )}

                                {/* Show dynamically limited default sizes */}
                                {defaultSizes.map((size) => (
                                  <SelectItem
                                    key={size}
                                    value={size.toString()}
                                  >
                                    {size}px
                                  </SelectItem>
                                ))}

                                <SelectItem value="custom">
                                  Custom Size
                                </SelectItem>
                              </>
                            );
                          })()}
                        </SelectContent>
                      </Select>
                      // <Select
                      //   value={fontSize.toString()}
                      //   onValueChange={(value: string) => {
                      //     if (value === "custom") {
                      //       setIsEditingCustomSize(true);
                      //       setPendingFontSize(
                      //         typeof localCustomFontSize === "number"
                      //           ? localCustomFontSize
                      //           : 24,
                      //       );
                      //     } else {
                      //       const newSize = parseInt(value);
                      //       setIsEditingCustomSize(false);
                      //       setFontSize(newSize);
                      //       // Don't update localCustomFontSize here - preserve user's custom value
                      //     }
                      //   }}
                      // >
                      //   <SelectTrigger className="w-full">
                      //     <SelectValue placeholder="Select font size" />
                      //   </SelectTrigger>
                      //   <SelectContent>
                      //     {(() => {
                      //       const hasCustomSize =
                      //         typeof localCustomFontSize === "number" &&
                      //         !defaultSizes.includes(localCustomFontSize);

                      //       return (
                      //         <>
                      //           {/* Show custom size if current fontSize is not in default list */}
                      //           {hasCustomSize && (
                      //             <SelectItem
                      //               key={`custom-${localCustomFontSize}`}
                      //               value={localCustomFontSize.toString()}
                      //             >
                      //               {localCustomFontSize}px (custom)
                      //             </SelectItem>
                      //           )}

                      //           {/* Show dynamically limited default sizes */}
                      //           {defaultSizes.map((size) => (
                      //             <SelectItem
                      //               key={size}
                      //               value={size.toString()}
                      //             >
                      //               {size}px
                      //             </SelectItem>
                      //           ))}

                      //           <SelectItem value="custom">
                      //             Custom Size
                      //           </SelectItem>
                      //         </>
                      //       );
                      //     })()}
                      //   </SelectContent>
                      // </Select>
                    )}

                    {/* Custom Size Input - only show when editing */}
                    {isEditingCustomSize && (
                      <div className="flex items-center gap-8">
                        <div className="flex items-center gap-2 ">
                          <input
                            type="number"
                            min="8"
                            max={maxFontSize}
                            value={
                              pendingFontSize !== null
                                ? pendingFontSize
                                : localCustomFontSize
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow empty string while typing
                              if (value === "") {
                                setPendingFontSize("");
                                return;
                              }

                              const numValue = parseInt(value);
                              // Store in pending state without updating actual fontSize
                              if (!isNaN(numValue)) {
                                setPendingFontSize(numValue);
                              }
                            }}
                            className=" border rounded-lg px-3 py-2 text-sm"
                            placeholder={`24`}
                            autoFocus
                          />
                          <span className="text-sm text-gray-600 font-medium">
                            px
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const value =
                                typeof pendingFontSize === "string"
                                  ? parseInt(pendingFontSize)
                                  : pendingFontSize;
                              const fallback = maxFontSize;

                              if (
                                value !== null &&
                                !isNaN(value) &&
                                value >= 8 &&
                                value <= maxFontSize
                              ) {
                                setLocalCustomFontSize(value);
                                setLocalFontSize(value);
                                setPendingFontSize(null);
                                setIsEditingCustomSize(false);
                              } else {
                                // fallback: use maxFontSize if over, 8 if under, or maxFontSize if invalid
                                const safeValue =
                                  value === null || isNaN(value)
                                    ? fallback
                                    : value > maxFontSize
                                      ? fallback
                                      : value < 8
                                        ? 8
                                        : fallback;
                                setLocalCustomFontSize(safeValue);
                                setLocalFontSize(safeValue);
                                setPendingFontSize(null);
                                setIsEditingCustomSize(false);
                              }
                            }}
                            className="p-2 bg-primary text-white hover:bg-primary/90 rounded transition-colors"
                            title="Apply font size"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setPendingFontSize(null);
                              setIsEditingCustomSize(false);
                            }}
                            className="p-2 bg-gray-100 text-black hover:bg-gray-200 rounded transition-colors"
                            title="Cancel changes"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Post Time Section */}
              {scheduledFor && setScheduledFor && (
                <div className="">
                  <label
                    className="text-sm text-gray-500"
                    style={{ fontWeight: "bolder", letterSpacing: "0.05em" }}
                  >
                    POST TIME
                  </label>
                  <div
                    className={`flex items-center space-x-2 mt-1 ${
                      isMobile ? "flex-col items-center space-y-2" : "flex-row"
                    }`}
                  >
                    <Popover
                      open={isCalendarOpen}
                      onOpenChange={setIsCalendarOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-[240px] justify-start text-left font-normal",
                            !date && "text-muted-foreground",
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? (
                            format(date, "PPP")
                          ) : (
                            <span>Change date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className={isMobile ? "w-auto p-0 max-h-[40vh] overflow-y-auto" : "w-auto p-0"}
                        align={isMobile ? "center" : "start"}
                        portalled={false}
                        collisionPadding={isMobile ? 16 : undefined}
                        avoidCollisions={true}
                      >
                        <CustomDatePicker
                          date={date}
                          setDate={setDate}
                          restriction="month"
                        />
                      </PopoverContent>
                    </Popover>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <Select
                        value={displayHour.toString()}
                        onValueChange={handleHourChange}
                      >
                        <SelectTrigger className="w-[70px]">
                          <SelectValue placeholder="Hour" />
                        </SelectTrigger>
                        <SelectContent className="cursor-pointer">
                          {hourOptions.map((hour) => (
                            <SelectItem key={hour} value={hour.toString()}>
                              {hour.toString().padStart(2, "0")}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={time.minute.toString()}
                        onValueChange={handleMinuteChange}
                      >
                        <SelectTrigger className="w-[70px]">
                          <SelectValue placeholder="Min" />
                        </SelectTrigger>
                        <SelectContent>
                          {minuteOptions.map((minute) => (
                            <SelectItem key={minute} value={minute.toString()}>
                              {minute.toString().padStart(2, "0")}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={period} onValueChange={handlePeriodChange}>
                        <SelectTrigger className="w-[70px]">
                          <SelectValue placeholder="AM/PM" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AM">AM</SelectItem>
                          <SelectItem value="PM">PM</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between pt-3">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="px-8 border-primary text-primary hover:bg-primary/10 text-md "
                  style={{ fontWeight: "bolder" }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!localImageText.trim()}
                  className="px-8 bg-blue-600 hover:bg-blue-700"
                  style={{ fontWeight: "bolder" }}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PostGraphicsEditor;